import { TaskNouns, TelecomPhoneLineId } from "@sp-crm/core";
import {
    AdvancedSearchEntityType,
    WorkflowEventType,
    WorkflowPayload,
    WorkflowStatus,
} from "generated/graphql";
import { CrmMode } from "util/crm-mode";
import {
    computeWorkflowDelay,
    entityTypeLabel,
    isTokenDelayRecipe,
} from "../manage-workflows-helper";

export interface WorkflowDelaySource {
    delay: number;
    delayRecipe: string | null;
}

export interface WorkflowChipSource extends WorkflowDelaySource {
    trigger: { eventType: WorkflowEventType };
    actions: ReadonlyArray<{
        __typename:
            | "WorkflowActionEmailEntity"
            | "WorkflowActionTaskEntity"
            | "WorkflowActionSmsEntity";
    }>;
}

const WAITLESS_EVENT_TYPES: WorkflowEventType[] = [
    WorkflowEventType.EmailOpened,
    WorkflowEventType.Schedule,
];

export type WizardStepKind = "email" | "task" | "sms";

export interface WorkflowStepChip {
    kind: WizardStepKind;
    delayLabel: string | null;
}

export function delayChipLabel(workflow: WorkflowDelaySource): string | null {
    if (isTokenDelayRecipe(workflow.delayRecipe)) {
        return null;
    }
    const [delay, unit] = computeWorkflowDelay(workflow);
    if (delay === 0) {
        return "Immediately";
    }
    return `+${delay}${unit}`;
}

export function workflowStepChips(workflow: WorkflowChipSource): WorkflowStepChip[] {
    const labelsDelay = WAITLESS_EVENT_TYPES.indexOf(workflow.trigger.eventType) === -1;
    return workflow.actions.map((action, index) => ({
        kind: stepChipKind(action.__typename),
        delayLabel: index === 0 && labelsDelay ? delayChipLabel(workflow) : null,
    }));
}

type WorkflowActionTypename = WorkflowChipSource["actions"][number]["__typename"];

function stepChipKind(typename: WorkflowActionTypename): WizardStepKind {
    switch (typename) {
        case "WorkflowActionTaskEntity":
            return "task";
        case "WorkflowActionSmsEntity":
            return "sms";
        case "WorkflowActionEmailEntity":
            return "email";
    }
}

export function wizardStepKind(
    actions: ReadonlyArray<{ __typename: WorkflowActionTypename }>,
): WizardStepKind | null {
    return actions.length === 1 ? stepChipKind(actions[0].__typename) : null;
}

interface SmsStepGaps {
    needsPhoneLine: boolean;
    needsConsent: boolean;
}

function smsStepGaps(payload: WorkflowPayload): SmsStepGaps | null {
    const sms =
        payload.actionEntities?.length === 1 ? payload.actionEntities[0].sms : null;
    if (!sms) {
        return null;
    }
    return {
        needsPhoneLine: !sms.fromPhoneLineId,
        needsConsent: !sms.consentAcknowledged,
    };
}

export function smsPublishBlocker(payload: WorkflowPayload): string | null {
    const gaps = smsStepGaps(payload);
    if (!gaps) {
        return null;
    }
    if (gaps.needsPhoneLine && gaps.needsConsent) {
        return "To publish, choose a sending phone number and confirm the consent acknowledgment on the Steps page.";
    }
    if (gaps.needsPhoneLine) {
        return "To publish, choose a sending phone number on the Steps page.";
    }
    if (gaps.needsConsent) {
        return "To publish, confirm the consent acknowledgment on the Steps page.";
    }
    return null;
}

export function stepContinueBlocker(payload: WorkflowPayload): string | null {
    const gaps = smsStepGaps(payload);
    if (!gaps) {
        return null;
    }
    if (gaps.needsPhoneLine && gaps.needsConsent) {
        return "Choose a sending phone number and confirm the consent acknowledgment to continue.";
    }
    if (gaps.needsPhoneLine) {
        return "Choose a sending phone number to continue.";
    }
    if (gaps.needsConsent) {
        return "Confirm the consent acknowledgment to continue.";
    }
    return null;
}

export interface SmsStepRequirements {
    fromPhoneLineId: TelecomPhoneLineId | null;
    consentAcknowledged: boolean;
}

export interface SmsPhoneLineAvailability {
    loaded: boolean;
    phoneLineCount: number;
}

export function smsStepBlocker(availability: SmsPhoneLineAvailability): string | null {
    if (!availability.loaded || availability.phoneLineCount > 0) {
        return null;
    }
    return "No active phone numbers are available in this workflow's regions. Claim or assign a phone number before setting up this text message.";
}

export type WizardSectionEditor =
    | "step-kind"
    | "email-sender"
    | "sms-sender"
    | "task-details"
    | "task-notification";

export type WizardSectionItem =
    | { type: "setting"; settingKey: string; label: "hidden" | "setting" }
    | { type: "editor"; editor: WizardSectionEditor };

export interface WizardStepSection {
    key: string;
    title: string;
    description: string | null;
    items: WizardSectionItem[];
}

export interface WizardSectionNouns {
    task: TaskNouns;
    entity: string;
}

const namedSetting = (settingKey: string): WizardSectionItem => ({
    type: "setting",
    settingKey,
    label: "hidden",
});

const labeledSetting = (settingKey: string): WizardSectionItem => ({
    type: "setting",
    settingKey,
    label: "setting",
});

const sectionEditor = (editor: WizardSectionEditor): WizardSectionItem => ({
    type: "editor",
    editor,
});

const stepKindSection = (): WizardStepSection => ({
    key: "stepKind",
    title: "What should this step do?",
    description: null,
    items: [sectionEditor("step-kind"), labeledSetting("delay")],
});

const emailSections = (): WizardStepSection[] => [
    {
        key: "emailRecipients",
        title: "Who should receive the email?",
        description: null,
        items: [namedSetting("recipients")],
    },
    {
        key: "emailContent",
        title: "What should the email say?",
        description: null,
        items: [namedSetting("emailTemplate"), labeledSetting("bccMyself")],
    },
    {
        key: "emailSender",
        title: "Who should send the email?",
        description: null,
        items: [sectionEditor("email-sender")],
    },
];

const smsSections = (): WizardStepSection[] => [
    {
        key: "smsRecipients",
        title: "Who should receive a text message?",
        description: null,
        items: [namedSetting("smsRecipients")],
    },
    {
        key: "smsContent",
        title: "What should the message say?",
        description: null,
        items: [namedSetting("smsBody")],
    },
    {
        key: "smsSender",
        title: "Which phone number should send the text message?",
        description: null,
        items: [sectionEditor("sms-sender")],
    },
];

const taskSections = (nouns: WizardSectionNouns): WizardStepSection[] => [
    {
        key: "taskDetails",
        title: `What should the ${nouns.task.singular} say?`,
        description: null,
        items: [namedSetting("description"), sectionEditor("task-details")],
    },
    {
        key: "taskDueDate",
        title: `When is the ${nouns.task.singular} due?`,
        description: "Days after the workflow runs.",
        items: [namedSetting("dueDateDays")],
    },
    {
        key: "taskAssignee",
        title: `Who should the ${nouns.task.singular} be assigned to?`,
        description: `Leave blank to use the ${nouns.entity}'s assigned user.`,
        items: [namedSetting("assignedTo")],
    },
    {
        key: "taskNotification",
        title: `How should we notify the ${nouns.task.singular} owner?`,
        description: null,
        items: [sectionEditor("task-notification")],
    },
];

const candidateSections = (
    kind: WizardStepKind | null,
    nouns: WizardSectionNouns,
): WizardStepSection[] => {
    switch (kind) {
        case "email":
            return [stepKindSection(), ...emailSections()];
        case "sms":
            return [stepKindSection(), ...smsSections()];
        case "task":
            return [stepKindSection(), ...taskSections(nouns)];
        case null:
            return [stepKindSection()];
    }
};

const claimedSettingKeys = (sections: WizardStepSection[]): string[] =>
    sections.flatMap(section =>
        section.items.flatMap(item => (item.type === "setting" ? [item.settingKey] : [])),
    );

export function wizardStepSections(
    kind: WizardStepKind | null,
    settingKeys: readonly string[],
    nouns: WizardSectionNouns,
): WizardStepSection[] {
    const candidates = candidateSections(kind, nouns);
    const claimed = claimedSettingKeys(candidates);
    const sections = candidates
        .map(section => ({
            ...section,
            items: section.items.filter(
                item => item.type === "editor" || settingKeys.includes(item.settingKey),
            ),
        }))
        .filter(section => section.items.length > 0);
    const unclaimed = settingKeys.filter(key => !claimed.includes(key));
    if (unclaimed.length === 0) {
        return sections;
    }
    return [
        ...sections,
        {
            key: "otherSettings",
            title: "Other settings",
            description: null,
            items: unclaimed.map(labeledSetting),
        },
    ];
}

export function wizardStepSettings<T extends { scope: string }>(
    settings: readonly T[],
): T[] {
    return settings.filter(s => s.scope === "tenant");
}

export type StepTimingMode = "immediately" | "wait";

export function stepTimingMode(delay: number): StepTimingMode {
    return delay === 0 ? "immediately" : "wait";
}

export function filterWorkflows<
    T extends {
        name: string;
        attachTo: AdvancedSearchEntityType;
        status: WorkflowStatus;
    },
>(
    workflows: readonly T[],
    entityTypeFilter: AdvancedSearchEntityType | "all",
    statusFilter: WorkflowStatus | "all",
    searchText: string,
): T[] {
    return workflows.filter(
        workflow =>
            (entityTypeFilter === "all" || workflow.attachTo === entityTypeFilter) &&
            (statusFilter === "all" || workflow.status === statusFilter) &&
            workflow.name.toLowerCase().includes(searchText.toLowerCase()),
    );
}

export function workflowsForEntityTypes<T extends { attachTo: AdvancedSearchEntityType }>(
    supportedEntityTypes: readonly AdvancedSearchEntityType[],
    workflows: readonly T[],
): T[] {
    return workflows.filter(workflow => supportedEntityTypes.includes(workflow.attachTo));
}

export function presentEntityTypes<T extends { attachTo: AdvancedSearchEntityType }>(
    supportedEntityTypes: readonly AdvancedSearchEntityType[],
    workflows: readonly T[],
): AdvancedSearchEntityType[] {
    return supportedEntityTypes.filter(entityType =>
        workflows.some(workflow => workflow.attachTo === entityType),
    );
}

export function sortWorkflowsByRecentlyUpdated<T extends { updatedAt: string }>(
    workflows: readonly T[],
): T[] {
    return [...workflows].sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    );
}

export function workflowEntityTypeLabel(
    attachTo: AdvancedSearchEntityType,
    mode: CrmMode,
): string {
    switch (attachTo) {
        case AdvancedSearchEntityType.Client:
            return mode === "combined" ? "Client - Placement" : "Client";
        case AdvancedSearchEntityType.HomeCareLead:
            return mode === "combined" ? "Client - Home Care" : "Client";
        default:
            return entityTypeLabel(attachTo);
    }
}
